import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface OrganizationMember {
  id: string;
  organization_id: string;
  email: string;
  full_name: string;
  role: string;
  auth_user_id: string | null;
  password_set: boolean;
  profile_picture_url?: string;
}

interface UseManagerAuthReturn {
  user: User | null;
  session: Session | null;
  member: OrganizationMember | null;
  organizationId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isMemberAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshMemberContext: () => Promise<void>;
}

export const useManagerAuth = (): UseManagerAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<OrganizationMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMemberByAuthId = async (authUserId: string): Promise<OrganizationMember | null> => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select('id, organization_id, email, full_name, role, auth_user_id, password_set, profile_picture_url')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching member by auth ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching member by auth ID:', error);
      return null;
    }
  };

  const fetchMemberByEmail = async (email: string): Promise<OrganizationMember | null> => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select('id, organization_id, email, full_name, role, auth_user_id, password_set, profile_picture_url')
        .eq('email', email.toLowerCase())
        .maybeSingle();

      if (error) {
        console.error('Error fetching member by email:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching member by email:', error);
      return null;
    }
  };

  const refreshMemberContext = async () => {
    const currentSession = session || (await supabase.auth.getSession()).data.session;
    
    if (!currentSession?.user) {
      console.log('useManagerAuth: No session, clearing member context');
      setMember(null);
      localStorage.removeItem('manager_member_id');
      localStorage.removeItem('manager_organization_id');
      sessionStorage.removeItem('organization_member_id');
      setIsLoading(false);
      return;
    }

    console.log('useManagerAuth: Refreshing member context for user:', currentSession.user.email);

    // First try to find member by auth_user_id
    let memberData = await fetchMemberByAuthId(currentSession.user.id);
    
    // If not found by auth_user_id, try by email (for new password setups)
    if (!memberData && currentSession.user.email) {
      console.log('useManagerAuth: Member not found by auth_user_id, trying by email');
      memberData = await fetchMemberByEmail(currentSession.user.email);
      
      // If found by email but auth_user_id is not set, update it
      if (memberData && !memberData.auth_user_id) {
        console.log('useManagerAuth: Updating member auth_user_id');
        
        const { data: updateResult, error: updateError } = await supabase
          .rpc('update_member_auth_user_id', {
            member_email: currentSession.user.email,
            new_auth_user_id: currentSession.user.id
          });

        if (updateError) {
          console.error('useManagerAuth: Error updating member auth_user_id:', updateError);
          toast.error('Error linking account');
        } else if (updateResult) {
          console.log('useManagerAuth: Successfully updated member auth_user_id');
          memberData.auth_user_id = currentSession.user.id;
        }
      }
    }

    if (memberData) {
      console.log('useManagerAuth: Member context established:', memberData);
      setMember(memberData);
      
      // Store in both localStorage and sessionStorage for persistence
      localStorage.setItem('manager_member_id', memberData.id);
      localStorage.setItem('manager_organization_id', memberData.organization_id);
      localStorage.setItem('manager_member_data', JSON.stringify(memberData));
      sessionStorage.setItem('organization_member_id', memberData.id);
      
      // Trigger storage event for other components listening
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'organization_member_id',
        newValue: memberData.id,
        oldValue: null
      }));
    } else {
      console.log('useManagerAuth: No member found for user:', currentSession.user.email);
      setMember(null);
      localStorage.removeItem('manager_member_id');
      localStorage.removeItem('manager_organization_id');
      localStorage.removeItem('manager_member_data');
      sessionStorage.removeItem('organization_member_id');
    }
    
    setIsLoading(false);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('useManagerAuth: Auth state changed:', event, session?.user?.email);
        
        if (event === 'SIGNED_OUT' || !session) {
          console.log('useManagerAuth: User signed out, clearing all state');
          setUser(null);
          setSession(null);
          setMember(null);
          localStorage.removeItem('manager_member_id');
          localStorage.removeItem('manager_organization_id');
          sessionStorage.clear();
          setIsLoading(false);
        } else {
          setUser(session.user);
          setSession(session);
          
          // Set session timeout (24 hours)
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            supabase.auth.signOut();
            toast.error('Session expired. Please log in again.');
          }, 24 * 60 * 60 * 1000); // 24 hours

          // Always refresh member context when auth state changes
          console.log('useManagerAuth: Refreshing member context for auth state change');
          refreshMemberContext();
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('useManagerAuth: Initial session check:', !!session, session?.user?.email);
      
      if (session) {
        setUser(session.user);
        setSession(session);
        
        // Try to recover member context from localStorage if available
        const cachedMemberData = localStorage.getItem('manager_member_data');
        if (cachedMemberData) {
          try {
            const memberData = JSON.parse(cachedMemberData);
            console.log('useManagerAuth: Recovered member context from cache:', memberData);
            setMember(memberData);
            sessionStorage.setItem('organization_member_id', memberData.id);
          } catch (error) {
            console.error('useManagerAuth: Error parsing cached member data:', error);
          }
        }
        
        refreshMemberContext();
      } else {
        console.log('useManagerAuth: No session, clearing all cache');
        localStorage.removeItem('manager_member_id');
        localStorage.removeItem('manager_organization_id');
        localStorage.removeItem('manager_member_data');
        sessionStorage.removeItem('organization_member_id');
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Remove member dependency to prevent infinite loop

  const signOut = async () => {
    try {
      localStorage.removeItem('manager_member_id');
      localStorage.removeItem('manager_organization_id');
      localStorage.removeItem('manager_member_data');
      sessionStorage.clear();
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setMember(null);
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out');
    }
  };

  return {
    user,
    session,
    member,
    organizationId: member?.organization_id || null,
    isLoading,
    isAuthenticated: !!session,
    isMemberAuthenticated: !!session && !!member,
    signOut,
    refreshMemberContext
  };
};